import { useEffect, useState, type CSSProperties, type SVGProps } from 'react'
import headerLogo from '../images/kamacioglu-sirketler-grubu-logo-light.png'
import { companies, content, type Language } from './data'

type IconProps = SVGProps<SVGSVGElement>
type CompanyAccentStyle = CSSProperties & { '--company-accent': string }

const ArrowRight = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const ArrowUpRight = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M7 17 17 7M8 7h9v9" />
  </svg>
)

const MenuIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
)

const CloseIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
)

const LocationIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
)

const PhoneIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M7.2 3h3l1.5 4.4-2.3 1.7a15 15 0 0 0 5.5 5.5l1.7-2.3L21 13.8v3c0 2.3-1.9 4.2-4.2 4.2C9.2 21 3 14.8 3 7.2 3 4.9 4.9 3 7.2 3Z" />
  </svg>
)

const MailIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m4 7 8 6 8-6" />
  </svg>
)

const SparkIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M12 2c.5 5.2 4.8 9.5 10 10-5.2.5-9.5 4.8-10 10C11.5 16.8 7.2 12.5 2 12c5.2-.5 9.5-4.8 10-10Z" />
  </svg>
)

function App() {
  const [language, setLanguage] = useState<Language>('tr')
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const t = content[language]

  const navItems = [
    { id: 'home', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'companies', label: t.nav.companies },
    { id: 'quality', label: t.nav.quality },
    { id: 'careers', label: t.nav.careers },
  ]

  useEffect(() => {
    document.documentElement.lang = language
    document.title = language === 'tr' ? 'Kamacıoğlu Şirketler Grubu' : 'Kamacıoğlu Group'

    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    if (meta) {
      meta.content =
        language === 'tr'
          ? 'Kamacıoğlu Şirketler Grubu — inşaat, cam, enerji, ticaret, turizm ve yaşam alanlarında geleceğe değer katan güçlü şirketler ağı.'
          : 'Kamacıoğlu Group — a connected network creating lasting value across construction, glass, energy, trade, tourism and living spaces.'
    }
  }, [language])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>('.reveal')
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 },
    )

    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [language])

  const selectLanguage = (nextLanguage: Language) => {
    setLanguage(nextLanguage)
    setMenuOpen(false)
  }

  const closeMenu = () => setMenuOpen(false)

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        {language === 'tr' ? 'İçeriğe geç' : 'Skip to content'}
      </a>

      <div className="shooting-stars" aria-hidden="true">
        {Array.from({ length: 8 }, (_, index) => (
          <span key={index} />
        ))}
      </div>

      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="header-inner">
          <a className="brand" href="#home" onClick={closeMenu} aria-label={t.nav.home}>
            <img className="brand-logo" src={headerLogo} alt="Kamacıoğlu Şirketler Grubu" />
          </a>

          <nav className={`main-nav ${menuOpen ? 'is-open' : ''}`} aria-label={language === 'tr' ? 'Ana menü' : 'Main menu'}>
            {navItems.map((item) => (
              <a key={item.id} href={`#${item.id}`} onClick={closeMenu}>
                {item.label}
              </a>
            ))}
            <a className="nav-contact" href="#contact" onClick={closeMenu}>
              {t.nav.contact}
              <ArrowUpRight />
            </a>
            <div className="mobile-language" aria-label={language === 'tr' ? 'Dil seçimi' : 'Language selection'}>
              <button className={language === 'tr' ? 'is-active' : ''} onClick={() => selectLanguage('tr')} type="button">
                TR
              </button>
              <span>/</span>
              <button className={language === 'en' ? 'is-active' : ''} onClick={() => selectLanguage('en')} type="button">
                EN
              </button>
            </div>
          </nav>

          <div className="header-actions">
            <div className="language-switch" aria-label={language === 'tr' ? 'Dil seçimi' : 'Language selection'}>
              <button className={language === 'tr' ? 'is-active' : ''} onClick={() => selectLanguage('tr')} type="button">
                TR
              </button>
              <span>/</span>
              <button className={language === 'en' ? 'is-active' : ''} onClick={() => selectLanguage('en')} type="button">
                EN
              </button>
            </div>
            <button
              className="menu-toggle"
              type="button"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? (language === 'tr' ? 'Menüyü kapat' : 'Close menu') : language === 'tr' ? 'Menüyü aç' : 'Open menu'}
              onClick={() => setMenuOpen((current) => !current)}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </header>

      <main id="main-content">
        <section className="hero" id="home">
          <h1 className="sr-only">{t.hero.title}</h1>
          <div className="hero-minimal reveal is-visible">
            <span className="hero-orbit hero-orbit-one" aria-hidden="true" />
            <span className="hero-orbit hero-orbit-two" aria-hidden="true" />
            <div className="hero-logo-cloud" aria-label={t.hero.networkLabel}>
              {companies.map((company) => (
                <a
                  className="hero-company-logo"
                  href={`#company-${company.id}`}
                  key={company.id}
                  aria-label={`${company.name} — ${company.sector[language]}`}
                  style={{ '--company-accent': company.accentRgb } as CompanyAccentStyle}
                >
                  <img src={company.logo} alt="" draggable="false" />
                </a>
              ))}
            </div>
            <div className="hero-core">
              <p className="hero-kicker">{t.hero.eyebrow}</p>
              <img className="hero-group-logo" src={headerLogo} alt="Kamacıoğlu Şirketler Grubu" />
              <p className="hero-tagline">{language === 'tr' ? 'Birlikte daha güçlü' : 'Stronger together'}</p>
            </div>
          </div>
        </section>

        <section className="stats-band" aria-label={language === 'tr' ? 'Kamacıoğlu sayılarla' : 'Kamacıoğlu in numbers'}>
          <div className="container stats-grid">
            {t.stats.map((stat, index) => (
              <div className="stat-item reveal" key={stat.label}>
                <span className="stat-index">0{index + 1}</span>
                <strong>{stat.value}</strong>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="section about-section" id="about">
          <div className="container">
            <div className="section-heading split-heading reveal">
              <div>
                <p className="eyebrow dark"><span />{t.about.eyebrow}</p>
                <h2>{t.about.title}</h2>
              </div>
              <p className="section-lead">{t.about.lead}</p>
            </div>

            <div className="about-grid">
              <article className="statement-card reveal">
                <span className="card-number">01</span>
                <div className="statement-icon"><SparkIcon /></div>
                <h3>{t.about.visionTitle}</h3>
                <p>{t.about.vision}</p>
              </article>
              <article className="statement-card statement-card-accent reveal">
                <span className="card-number">02</span>
                <div className="statement-icon"><ArrowUpRight /></div>
                <h3>{t.about.missionTitle}</h3>
                <p>{t.about.mission}</p>
              </article>
              <article className="values-card reveal">
                <div>
                  <span className="card-number">03</span>
                  <h3>{t.about.valuesTitle}</h3>
                </div>
                <ul>
                  {t.about.values.map((value, index) => (
                    <li key={value}>
                      <span>0{index + 1}</span>
                      {value}
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="section companies-section" id="companies">
          <div className="container">
            <div className="section-heading split-heading reveal">
              <div>
                <p className="eyebrow"><span />{t.companies.eyebrow}</p>
                <h2>{t.companies.title}</h2>
              </div>
              <p className="section-lead">{t.companies.description}</p>
            </div>

            <div className="companies-grid">
              {companies.map((company, index) => (
                <article
                  className="company-card reveal"
                  id={`company-${company.id}`}
                  key={company.id}
                  style={{ '--company-accent': company.accentRgb } as CompanyAccentStyle}
                >
                  <div className="company-card-top">
                    <span className="company-order">{String(index + 1).padStart(2, '0')}</span>
                    <div className="company-logo-wrap">
                      <img src={company.logo} alt={`${company.name} logo`} />
                    </div>
                  </div>
                  <div className="company-card-body">
                    <p className="company-sector">{company.sector[language]}</p>
                    <h3>{company.name}</h3>
                    <p>{company.description[language]}</p>
                  </div>
                  {company.website ? (
                    <a className="company-link" href={company.website} target="_blank" rel="noreferrer">
                      {t.companies.visit}
                      <ArrowUpRight />
                    </a>
                  ) : (
                    <span className="company-link company-link-muted">
                      {t.companies.profile}
                      <span className="status-dot" />
                    </span>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section quality-section" id="quality">
          <div className="quality-grid" aria-hidden="true" />
          <div className="container">
            <div className="quality-intro reveal">
              <p className="eyebrow"><span />{t.quality.eyebrow}</p>
              <h2>{t.quality.title}</h2>
              <p>{t.quality.description}</p>
            </div>
            <div className="quality-list">
              {t.quality.pillars.map((pillar) => (
                <article className="quality-row reveal" key={pillar.number}>
                  <span>{pillar.number}</span>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.text}</p>
                  <div className="quality-arrow"><ArrowUpRight /></div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section careers-section" id="careers">
          <div className="container careers-card reveal">
            <div className="careers-copy">
              <p className="eyebrow dark"><span />{t.careers.eyebrow}</p>
              <h2>{t.careers.title}</h2>
              <p>{t.careers.description}</p>
              <a className="button button-dark" href="#contact">
                {t.careers.cta}
                <ArrowRight />
              </a>
            </div>
            <div className="careers-visual" aria-hidden="true">
              <span className="careers-number">750</span>
              <span className="careers-plus">+</span>
              <div className="orbit orbit-one" />
              <div className="orbit orbit-two" />
              <div className="orbit-dot dot-one" />
              <div className="orbit-dot dot-two" />
            </div>
            <p className="careers-note">{t.careers.note}</p>
          </div>
        </section>

        <section className="section contact-section" id="contact">
          <div className="container contact-grid">
            <div className="contact-copy reveal">
              <p className="eyebrow"><span />{t.contact.eyebrow}</p>
              <h2>{t.contact.title}</h2>
              <div className="contact-list">
                <div className="contact-item">
                  <span className="contact-icon"><LocationIcon /></span>
                  <div>
                    <small>{t.contact.addressLabel}</small>
                    <p>{t.contact.address}</p>
                  </div>
                </div>
                <a className="contact-item" href="tel:+903924440707">
                  <span className="contact-icon"><PhoneIcon /></span>
                  <div>
                    <small>{t.contact.phoneLabel}</small>
                    <p>{t.contact.phone}</p>
                  </div>
                </a>
                <a className="contact-item" href="mailto:info@kamacioglugroup.com">
                  <span className="contact-icon"><MailIcon /></span>
                  <div>
                    <small>{t.contact.emailLabel}</small>
                    <p>{t.contact.email}</p>
                  </div>
                </a>
              </div>
            </div>

            <form className="contact-form reveal" onSubmit={(event) => event.preventDefault()}>
              <div className="form-heading">
                <h3>{t.contact.formTitle}</h3>
                <p>{t.contact.formNote}</p>
              </div>
              <div className="field-row">
                <label>
                  <span>{t.contact.name}</span>
                  <input type="text" name="name" autoComplete="name" placeholder={t.contact.name} />
                </label>
                <label>
                  <span>{t.contact.emailField}</span>
                  <input type="email" name="email" autoComplete="email" placeholder="ornek@email.com" />
                </label>
              </div>
              <label>
                <span>{t.contact.phoneField}</span>
                <input type="tel" name="phone" autoComplete="tel" placeholder="+90" />
              </label>
              <label>
                <span>{t.contact.message}</span>
                <textarea name="message" rows={4} placeholder={t.contact.message} />
              </label>
              <button className="form-submit" type="submit" disabled>
                {t.contact.submit}
                <ArrowRight />
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-main">
          <a className="brand footer-brand" href="#home" aria-label={t.nav.home}>
            <img className="brand-logo" src={headerLogo} alt="Kamacıoğlu Şirketler Grubu" />
          </a>
          <div className="footer-links">
            {navItems.map((item) => (
              <a key={item.id} href={`#${item.id}`}>{item.label}</a>
            ))}
          </div>
          <a className="back-to-top" href="#home" aria-label={language === 'tr' ? 'Yukarı dön' : 'Back to top'}>
            <ArrowUpRight />
          </a>
        </div>
        <div className="container footer-bottom">
          <p>© {new Date().getFullYear()} {t.footer}</p>
          <p>Lefkoşa · Kuzey Kıbrıs</p>
        </div>
      </footer>
    </div>
  )
}

export default App
